import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import {
  hasLiveChatManagePermission,
  requestLiveChatBrowserNotificationPermission,
  saveLiveChatNotificationSubscription,
  setLiveChatNotificationPromptStatus,
  showLiveChatBrowserNotification,
  shouldAskLiveChatNotificationPermission,
} from 'src/utils/liveChatNotifications';
import { useLocation } from 'react-router-dom';
import { addLiveChatSocketListener, connectLiveChatSocket } from 'src/utils/liveChatSocket';
import {
  incrementLiveChatUnreadCount,
  resetLiveChatUnreadCount,
} from 'src/utils/liveChatUnread';
import {
  incrementTicketUnreadCount,
  resetTicketUnreadCount,
} from 'src/utils/ticketUnread';
import { showSupportAppNotification } from './SupportAppNotification';

export default function LiveChatNotificationBridge() {
  const disposedRef = useRef(false);
  const isAgentInboxRouteRef = useRef(false);
  const isTicketsRouteRef = useRef(false);
  const notifiedTicketIdsRef = useRef(new Set());
  const [notificationPromptOpen, setNotificationPromptOpen] = useState(false);
  const [enableNotificationsLoading, setEnableNotificationsLoading] = useState(false);
  const location = useLocation();
  const isAgentInboxRoute = location.pathname === '/agent-inbox';
  const isTicketsRoute = location.pathname === '/tickets';
  const getPayloadBrandId = (payload) => Number(payload.brand_id || payload.brandId || 0);

  const appURL = (path) => {
    try {
      return new URL(path, window.location.origin).toString();
    } catch (_error) {
      return path;
    }
  };

  const showSupportNotification = (detail, browserTitle, browserBody, browserOptions = {}) => {
    showSupportAppNotification(detail);
    showLiveChatBrowserNotification(browserTitle, browserBody, browserOptions);
  };

  useEffect(() => {
    isAgentInboxRouteRef.current = isAgentInboxRoute;
    if (isAgentInboxRoute) {
      resetLiveChatUnreadCount();
    }
  }, [isAgentInboxRoute]);

  useEffect(() => {
    isTicketsRouteRef.current = isTicketsRoute;
    if (isTicketsRoute) {
      resetTicketUnreadCount();
    }
  }, [isTicketsRoute]);

  const connectSocket = useCallback(() => {
    if (disposedRef.current) {
      return;
    }
    connectLiveChatSocket();
  }, []);

  useEffect(() => {
    if (!hasLiveChatManagePermission()) {
      return undefined;
    }

    const removeListener = addLiveChatSocketListener((data) => {
      const payload = data.payload || {};
      const eventBrandId = getPayloadBrandId(payload);
      const ticketEventTypes = ['ticket:new', 'ticket:message:new'];
      if (ticketEventTypes.includes(data.type)) {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('brand2chat:tickets-refresh', {
            detail: { type: data.type, payload },
          }));
        }

        if (!isTicketsRouteRef.current) {
          incrementTicketUnreadCount(eventBrandId);
          if (data.type === 'ticket:new') {
            const ticketNo = String(payload.ticket_no || '').trim();
            const messageBody =
              String(payload.subject || '').trim() ||
              String(payload.requester_email || payload.requester_name || '').trim() ||
              'New ticket created';
            const title = ticketNo ? `New ticket ${ticketNo}` : 'New ticket';
            const ticketId = Number(payload.ticket_id || 0);
            const ticketUrl = appURL(ticketId ? `/tickets?ticket_id=${ticketId}` : '/tickets');
            if (ticketId) {
              notifiedTicketIdsRef.current.add(ticketId);
            }
            showSupportNotification({
              type: 'ticket',
              title,
              message: messageBody,
              url: ticketUrl,
            }, title, messageBody, {
              force: true,
              url: ticketUrl,
            });
          }
        } else {
          resetTicketUnreadCount(eventBrandId);
        }
        return;
      }
      if (data.type === 'notification:new' && payload.type === 'ticket:created') {
        if (!isTicketsRouteRef.current) {
          const ticketId = Number(payload.ticket_id || 0);
          if (ticketId && notifiedTicketIdsRef.current.has(ticketId)) {
            return;
          }
          const ticketUrl = appURL(ticketId ? `/tickets?ticket_id=${ticketId}` : '/tickets');
          if (ticketId) {
            notifiedTicketIdsRef.current.add(ticketId);
          }
          showSupportNotification({
            type: 'ticket',
            title: payload.title || 'Ticket Created',
            message: payload.body || 'A live chat timed out, so a support ticket was created.',
            url: ticketUrl,
          }, payload.title || 'Ticket Created', payload.body || 'A live chat timed out, so a support ticket was created.', {
            force: true,
            url: ticketUrl,
          });
        }
        return;
      }
      if (
        data.type === 'notification:new' &&
        (payload.type === 'live_chat_new_visitor' || payload.type === 'conversation:new')
      ) {
        const notificationTitle = payload.title || 'New Live Chat Visitor';
        const notificationBody = payload.body || 'A new visitor started a live chat';
        const conversationId = Number(payload.conversation_id || 0);
        const visitorUrl = appURL(conversationId ? `/agent-inbox?conversation_id=${conversationId}` : '/agent-inbox');
        showSupportNotification({
          type: 'visitor',
          title: notificationTitle,
          message: notificationBody || notificationTitle || 'New live chat notification',
          url: visitorUrl,
        }, notificationTitle, notificationBody, {
          force: true,
          url: visitorUrl,
        });
        return;
      }
      if (data.type === 'message:new' && payload.sender_type === 'visitor') {
        if (!isAgentInboxRouteRef.current) {
          incrementLiveChatUnreadCount(eventBrandId);
          const messageBody =
            String(payload.message || '').trim() ||
            (String(payload.media_url || '').trim()
              ? 'Visitor sent a media message'
              : 'New visitor message');
          const conversationId = Number(payload.conversation_id || 0);
          const messageUrl = appURL(conversationId ? `/agent-inbox?conversation_id=${conversationId}` : '/agent-inbox');
          showSupportNotification({
            type: 'message',
            title: 'New visitor message',
            message: messageBody,
            url: messageUrl,
          }, 'New Visitor Message', messageBody, {
            force: true,
            url: messageUrl,
          });
        }
      }
    });

    return removeListener;
  }, []);

  useEffect(() => {
    if (!hasLiveChatManagePermission()) {
      return undefined;
    }

    connectSocket();
    if (typeof Notification !== 'undefined' && shouldAskLiveChatNotificationPermission()) {
      setNotificationPromptOpen(true);
    }

    return () => {
      disposedRef.current = true;
    };
  }, [connectSocket]);

  const handleEnableNotifications = async () => {
    if (enableNotificationsLoading) {
      return;
    }

    setEnableNotificationsLoading(true);
    try {
      const permission = await requestLiveChatBrowserNotificationPermission();
      setNotificationPromptOpen(false);
      if (permission === 'granted') {
        disposedRef.current = false;
        connectSocket();
      }
    } finally {
      setEnableNotificationsLoading(false);
    }
  };

  const handleSkipNotifications = () => {
    if (enableNotificationsLoading) {
      return;
    }

    setLiveChatNotificationPromptStatus('dismissed');
    saveLiveChatNotificationSubscription(
      false,
      typeof Notification !== 'undefined' ? Notification.permission : 'denied',
      { force: true }
    );
    setNotificationPromptOpen(false);
  };

  return (
    <Dialog
      open={notificationPromptOpen}
      onClose={enableNotificationsLoading ? undefined : handleSkipNotifications}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>Enable browser notifications?</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary">
          Get alerted when a new visitor starts a live chat or replies while you are on another page.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleSkipNotifications} color="inherit" disabled={enableNotificationsLoading}>
          Skip
        </Button>
        <Button
          onClick={handleEnableNotifications}
          variant="contained"
          disabled={enableNotificationsLoading}
          startIcon={
            enableNotificationsLoading ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {enableNotificationsLoading ? 'Enabling...' : 'Enable'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
